import * as React from 'react';
import { Button, Form, Modal, Row, Col } from 'antd';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { DocumentService } from 'src/app/shared/document.service';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { LanguageEditorController } from 'src/app/general/language/parts/language-editor.controller';
import { DocumentTemplateEditorController } from 'src/app/general/document-templates/parts/document-template-editor.controller';
import { DocumentTemplateSearchController } from 'src/app/general/document-template-search/document-template-search.controller';
import { IdentityService } from 'src/core/services/identity.service';

interface DocumentGeneratorViewProps {
	mode?: ControlMode;
	setBusy(isBusy: boolean);
	targetId: number;
	usage: DocumentGenerationUsage;
	marketId?: number;
	brandId?: number;
	afterGenerate: () => void;
	generationEnabled?: boolean;
}

interface DocumentGeneratorViewState {
	exportLanguage: string;
	format: DocumentGenerationFormat;
	templateId?: number;
	showModal: boolean;
}

@observer
export class DocumentGeneratorView extends React.Component<DocumentGeneratorViewProps, DocumentGeneratorViewState> {
	@resolve
	private toast: ToastNotifications;

	@resolve
	private documentService: DocumentService;

	@resolve
	private identity: IdentityService;

	constructor(props: Readonly<DocumentGeneratorViewProps>) {
		super(props);

		this.state = {
			exportLanguage: 'en',
			format: 'PDF',
			showModal: false,
		};
	}
	
	private acceptNotesDialog = (ev?: React.MouseEvent<HTMLButtonElement>) => {
		if (ev != null) {
			ev.preventDefault();
		}

		this.setState({ showModal: false });
	}

	private generateDocument = async (targetId: number, usage: string, templateId: number, format: string, exportLanguage: string): Promise<void> => {
		this.props.setBusy(true);
		try {
			await this.documentService.generate(targetId, usage, templateId, format, exportLanguage);
			this.toast.showToast('Document is generating', 'When it is done, the document list will be reloaded', 'info');
			this.props.afterGenerate();
		} catch (err) {
			this.toast.showToast('Document generation failed!', ObjectUtils.parseError(err), 'error');
		} finally {
			this.props.setBusy(false);
		}
	}

	render() {
		const mode = this.props.mode || 'edit';
		const { showModal, exportLanguage, templateId, format } = this.state;
		if (!this.identity.canGenerateDocuments) {
			return null;
		}

		return (
			<div>
				<Modal
					style={{ top: 20 }}
					width="80%"
					visible={showModal}
					onCancel={this.acceptNotesDialog}
					title="Generated"
					maskClosable={false}
					footer={[
						<Button
							key="confirm"
							type="primary"
							onClick={this.acceptNotesDialog}>
							OK
						</Button>
					]}>
					<DocumentTemplateSearchController
						model={this.props.targetId}
						mode={mode}
						usage={this.props.usage}
						setBusy={this.props.setBusy}
						generateDocument={this.generateDocument}
						marketId={this.props.marketId}
						brandId={this.props.brandId}
					/>
				</Modal>
				<Row gutter={16}>
					<Col xs={24} lg={6}>
						<LanguageEditorController
							mode={mode}
							required
							value={exportLanguage}
							onChanged={(e => this.setState({ exportLanguage: e }))}
							setBusy={this.props.setBusy}
						/>
					</Col>
						<Col xs={24} lg={8}>
						<DocumentTemplateEditorController
							mode={mode}
							required
							value={templateId}
							onChanged={(e => this.setState({ templateId: e ? e.id : null }))}
							setBusy={this.props.setBusy}
							usage={this.props.usage}
							marketId={this.props.marketId}
							brandId={this.props.brandId}
						/>
					</Col>
					<Col xs={24} lg={6}>
						<DropdownView
							label="Format"
							disabled={mode === 'display'}
							model={format}
							required
							onChanged={(item) => { this.setState({ format: item.value }) }}
							options={
								[
									{ value: 'PDF', label: 'PDF' },
									{ value: 'WORDOPENXML', label: 'WORD' },
									{ value: 'EXCELOPENXML', label: 'EXCEL' },
								]
							}
						/>
					</Col>
					<Col xs={24} lg={4}>
						<Form.Item label="&nbsp;">
							<Button.Group>
								<Button
									disabled={templateId == null}
									onClick={() => {
										this.generateDocument(this.props.targetId, this.props.usage, templateId, format, exportLanguage);
									}}>
									Generate
									</Button>
								<Button
									disabled={mode === 'display'}
									onClick={() => this.setState({ showModal: true })}>
									...
								</Button>
							</Button.Group>
						</Form.Item>
					</Col>
				</Row>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/document-generation/document-generator.view.tsx